import React, { useEffect, useRef, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import AOS from "aos";
import "aos/dist/aos.css";
import Footer from "../components/Footer";
import Navbar from "../components/home-components/Navbar";
import HeroOne from "../components/home-components/HeroOne";
import { SearchProvider } from "../components/home-components/SearchContext";
import HighlightText from "../components/home-components/HighlightText";
import CourseDiscovery from "../components/landing/CourseDiscovery";
import CommercialSubjects from "../components/landing/CommercialSubjects";
import CartToast from "../components/landing/CartToast";
import {
  TrendingNow,
} from "../components/landing/DiscoverySections";
import {MaterialsSection} from "../components/landing/LandingMaterials";
import TestimonialsPage from "./TestimonialsPage";
import TutorPage from "./TutorPage";
import NewsPage from "./NewsPage";
import AuthenticatedLanding from "../components/landing/AuthenticatedLanding";
import { useUserAuth } from "../store/user";

/* ───────────────── STATS (retained) ───────────────── */

const STATS = [
  { end: 50, suffix: "K+", label: "Students", color: "text-black" },
  { end: 95, suffix: "%", label: "Success Rate", color: "text-black" },
  { end: 10000, suffix: "+", label: "Questions", color: "text-black" },
  { end: 200, suffix: "+", label: "Courses", color: "text-black" },
  { end: 60, suffix: "+", label: "Subjects", color: "text-black" },
  { end: 24, suffix: "/7", label: "AI Assistance", color: "text-black" },
];

/* ───────────────── SCROLL-ANIMATED COUNTER ───────────────── */

function useInView(threshold = 0.4) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.unobserve(el); // fire once
        }
      },
      { threshold }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, inView];
}

function AnimatedCount({ end, suffix = "", duration = 2000 }) {
  const [ref, inView] = useInView(0.3);
  const [count, setCount] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    if (!inView || started.current) return;
    started.current = true;

    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [inView, end, duration]);

  return (
    <span ref={ref}>
      {suffix === "K+" ? `${count}K+` : suffix === "%" ? `${count}%` : suffix === "/7" ? `${count}/7` : `${count.toLocaleString()}${suffix}`}
    </span>
  );
}

function StatsSection() {
  return (
    <div className="font-poppins w-full overflow-x-hidden bg-blue-50">

    <div className="grid lg:grid-cols-6 grid-cols-2  justify-start items-start max-w-3xl m-auto py-3 border-t border-slate-200">
      {STATS.map((stat, i) => (
        <div key={i} className="text-center lg:text-left">
          <div className={`text-3xl lg:text-2xl  ${stat.color} -mb-1`}>
            <AnimatedCount end={stat.end} suffix={stat.suffix} />
          </div>
          <div className="text-sm text-slate-600 dark:text-slate-400 font-medium">
            <HighlightText text={stat.label} tag="span" />
          </div>
        </div>
      ))}
    </div>
    </div>

  );
}


/* ───────────────── AUTH-AWARE LANDING ───────────────── */

const SESSION_STORE_KEY = "fillop_session";

/**
 * True when this browser has a remembered session (mock Google sign-in or a
 * real learner login). Reads only storage — never hits the network — so the
 * public landing page stays session-free.
 */
function hasRememberedSession() {
  try {
    const stored = JSON.parse(window.sessionStorage.getItem(SESSION_STORE_KEY) || "null");
    if (stored?.token) return true;
  } catch {
    /* ignore */
  }
  try {
    return Boolean(
      window.localStorage.getItem("name") ||
        window.localStorage.getItem("uemail") ||
        window.localStorage.getItem("email")
    );
  } catch {
    return false;
  }
}

/**
 * Logged out → the original marketing landing page.
 * Logged in (incl. the mock Google One Tap sign-in) → the authenticated landing.
 */
function useLandingAuthed() {
  const { userIsAuthenticated } = useUserAuth();
  const [authed, setAuthed] = useState(() => userIsAuthenticated || hasRememberedSession());

  useEffect(() => {
    setAuthed(userIsAuthenticated || hasRememberedSession());
  }, [userIsAuthenticated]);

  return authed;
}

function HomeContent() {
  const authed = useLandingAuthed();

  if (authed) {
    return <AuthenticatedLanding />;
  }

  return (
    <div className="overflow-x-hidden max-w-full">
      <Navbar />
      <main>
        <HeroOne />
      </main>

      {/* Primary marketplace: search, categories, filters, sort, grid */}
      <CourseDiscovery />

      {/* "Explore Commercial Subjects" — fully redesigned */}
      <CommercialSubjects />

      {/* Public marketplace materials — filtered in place by type */}
      <MaterialsSection />

      {/* Additional course discovery content */}
      <TrendingNow />

      {/* Retained sections (not on the remove list) */}
      <div data-aos="fade-up">
        <StatsSection />
      </div>



      <div data-aos="fade-up">
        <TestimonialsPage />
      </div>

      <div data-aos="fade-up">
        <TutorPage />
      </div>

      <div data-aos="fade-up" data-aos-delay="100">
        <NewsPage />
      </div>

      <Footer />
      <CartToast />
    </div>
  );
}

/* ───────────────── ROOT ───────────────── */

const Home = () => {
  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true, // Animations will only trigger once
    });
  }, []);

  return (
    <SearchProvider>
      <HomeContent />
    </SearchProvider>
  );
};

export default Home;
